import { Controller } from "@hotwired/stimulus"

// Global chat drawer. Persists open state + active conversation in
// localStorage so both survive the full-page navigation that happens when a
// message is sent. ⌘J / Ctrl+J toggles. The active conversation lazy-loads
// into the turbo-frame via its `src`.
export default class extends Controller {
  static targets = [ "panel", "backdrop", "toggle", "frame", "switcher", "resizer" ]
  static values  = {
    storageKey: { type: String, default: "chat-drawer-open" },
    convoKey:   { type: String, default: "chat-drawer-convo" },
    widthKey:   { type: String, default: "chat-drawer-width" },
    base:       { type: String, default: "/company/drawer" }
  }

  MIN_WIDTH = 320
  DEFAULT_WIDTH = 440

  connect() {
    this._onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "j" || e.key === "J")) {
        e.preventDefault(); this.toggle()
      }
      if (e.key === "Escape" && this._isOpen()) this.close()
    }
    document.addEventListener("keydown", this._onKey)

    // After a send/switch the chat frame reloads — scroll to the latest.
    this._onFrameRender = (e) => {
      if (e.target && e.target.id === "drawer-frame") this._scrollSoon()
    }
    document.addEventListener("turbo:frame-render", this._onFrameRender)

    // Bound resize handlers (so we can add/remove them on document).
    this._onMove = this._onMove.bind(this)
    this._onUp   = this._onUp.bind(this)
    this._applyWidth()
    window.addEventListener("resize", () => this._applyWidth())

    // Restore the last conversation into the switcher + frame.
    const convo = this._convo()
    this._syncSwitcher(convo)
    this._loadConvo(convo)

    if (localStorage.getItem(this.storageKeyValue) === "1") this.open({ persist: false })
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
    document.removeEventListener("mousemove", this._onMove)
    document.removeEventListener("mouseup", this._onUp)
    if (this._onFrameRender) document.removeEventListener("turbo:frame-render", this._onFrameRender)
  }

  // ── Resize ────────────────────────────────────────────────────────
  startResize(event) {
    event.preventDefault()
    this._resizing = true
    document.addEventListener("mousemove", this._onMove)
    document.addEventListener("mouseup", this._onUp)
    document.body.style.userSelect = "none"
    document.body.style.cursor = "ew-resize"
  }

  _onMove(event) {
    if (!this._resizing) return
    const w = this._clamp(window.innerWidth - event.clientX)
    this.panelTarget.style.width = `${w}px`
  }

  _onUp() {
    if (!this._resizing) return
    this._resizing = false
    document.removeEventListener("mousemove", this._onMove)
    document.removeEventListener("mouseup", this._onUp)
    document.body.style.userSelect = ""
    document.body.style.cursor = ""
    const w = parseInt(this.panelTarget.style.width, 10)
    if (w) localStorage.setItem(this.widthKeyValue, String(w))
  }

  resetWidth(event) {
    if (event) event.preventDefault()
    localStorage.removeItem(this.widthKeyValue)
    this._applyWidth()
  }

  _clamp(w) {
    const max = Math.floor(window.innerWidth * 0.95)
    return Math.max(this.MIN_WIDTH, Math.min(w, max))
  }

  _applyWidth() {
    if (!this.hasPanelTarget) return
    // Below the sm breakpoint the drawer is full-width — don't pin a px width.
    if (window.innerWidth < 640) {
      this.panelTarget.style.width = ""
      return
    }
    const saved = parseInt(localStorage.getItem(this.widthKeyValue), 10)
    const w = this._clamp(saved || this.DEFAULT_WIDTH)
    this.panelTarget.style.width = `${w}px`
  }

  toggle(event) {
    if (event) event.preventDefault()
    this._isOpen() ? this.close() : this.open()
  }

  open({ persist = true } = {}) {
    this.panelTarget.classList.remove("hidden")
    if (this.hasBackdropTarget) this.backdropTarget.classList.remove("hidden")
    if (this.hasToggleTarget) this.toggleTarget.classList.add("hidden")
    if (persist) localStorage.setItem(this.storageKeyValue, "1")
    // Ensure the frame has loaded the active conversation.
    if (this.hasFrameTarget && !this.frameTarget.getAttribute("src")) {
      this._loadConvo(this._convo())
    }
    this._focusInputSoon()
    this._scrollSoon()
  }

  close() {
    this.panelTarget.classList.add("hidden")
    if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
    if (this.hasToggleTarget) this.toggleTarget.classList.remove("hidden")
    localStorage.setItem(this.storageKeyValue, "0")
    // The mic lives in the drawer; closing it must stop voice so we're not
    // listening in the background with no visible control.
    document.dispatchEvent(new CustomEvent("drawer:close"))
  }

  // <chat-switcher> change event → swap the conversation.
  switch(event) {
    const key = event.detail?.key
    if (!key) return
    localStorage.setItem(this.convoKeyValue, key)
    this._loadConvo(key)
    this._focusInputSoon()
    this._scrollSoon()
  }

  // Keep the web component's displayed selection in sync (silent — no event,
  // so we don't trigger a reload loop).
  _syncSwitcher(key) {
    if (!this.hasSwitcherTarget) return
    const el = this.switcherTarget
    if (typeof el.setValue === "function") {
      el.setValue(key, { silent: true })
    } else {
      // Component not yet upgraded — set the attribute so connectedCallback picks it up.
      el.setAttribute("value", key)
    }
  }

  _loadConvo(key) {
    if (!this.hasFrameTarget) return
    const url = `${this.baseValue}?convo=${encodeURIComponent(key)}`
    // Force a reload even if src is unchanged (e.g. after sending a message).
    this.frameTarget.removeAttribute("complete")
    this.frameTarget.setAttribute("src", url)
  }

  _convo() {
    return localStorage.getItem(this.convoKeyValue) || "jarvis"
  }

  _isOpen() {
    return !this.panelTarget.classList.contains("hidden")
  }

  _focusInputSoon() {
    setTimeout(() => {
      const ta = document.getElementById("drawer-textarea")
      if (ta) ta.focus()
    }, 120)
  }

  _scrollSoon() {
    setTimeout(() => {
      const bottom = document.getElementById("drawer-feed-bottom")
      if (bottom) bottom.scrollIntoView({ block: "end" })
    }, 150)
  }
}
